import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import useAxios from '../../../../hooks/useAxios'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    Image,
    mergeClasses,
    Tooltip,
} from '@fluentui/react-components'
import { HammockDialogProps } from '../HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import Loading from '../../../../components/Loading'
import { useApplicationStyles } from '../../../../pages/App'
import LoadingIcon from '../../../../components/Icon/LoadingIcon'
import { ArrowLeftFilled, ArrowRightFilled, bundleIcon, DismissFilled, DismissRegular, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import useOpenDownloadDriveItem from '../../../hooks/useOpenDownloadDriveItem'
import InlineSelfSubmittingInput from '../../Input/InlineSelfSubmittingInput'
import { AxiosResponse } from 'axios'
import { getDocumentExtension, getDocumentNameFromPath, getExtensionChange, previewFileTypes } from '../../../../helpers/documentHelper'
import { formatInputDocumentName } from '../../../../helpers/regexHelper'

const ArrowLeftIcon = bundleIcon(ArrowLeftFilled, ArrowLeftFilled)
const ArrowRightIcon = bundleIcon(ArrowRightFilled, ArrowRightFilled)
const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

export type PreviewDocumentDialogBodyProps = DialogBodyProps & {
    driveItem: DriveItem

    callback?: () => void
    hasPrevious?: () => boolean
    hasNext?: () => boolean
    previous?: () => void
    next?: () => void
}

export default function PreviewDocumentDialogBody(props: HammockDialogProps<PreviewDocumentDialogBodyProps>) {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as PreviewDocumentDialogBodyProps), [props])

    const [loading, setLoading] = useState<boolean>(false)
    const [editingName, setEditingName] = useState<boolean>(false)
    const [preview, setPreview] = useState<string>(undefined)
    const isValid = useMemo(() => previewFileTypes.includes(getDocumentExtension(dialogBodyProps.driveItem?.name)), [dialogBodyProps.driveItem])
    const { setDriveItem: setOpeningDriveItem, loaded: openingDriveItemLoaded, isOpening, openOrDownload } = useOpenDownloadDriveItem()

    useEffect(() => {
        console.log('PreviewDocumentDialogBody', dialogBodyProps)

        setPreview(undefined)
        setOpeningDriveItem(dialogBodyProps.driveItem)
        setLoading(false)

        if (dialogBodyProps.driveItem && isValid) {
            setLoading(true)
            axios
                .get(`/items/${dialogBodyProps?.driveItem.id}/thumbnails/0/large`, {
                    baseURL: Destination.Documents,
                })
                .then(response => {
                    setPreview(response.data.url)
                    setLoading(false)
                })
        } else {
            setPreview('')
        }
    }, [dialogBodyProps.driveItem])

    if (!dialogBodyProps.driveItem) {
        return <></>
    }

    return (
        <DialogBody className={mergeClasses(appStyles.overflowXHidden, appStyles.height100)}>
            <DialogTitle>
                <InlineSelfSubmittingInput
                    disabled={loading}
                    isEditing={editingName}
                    setIsEditing={setEditingName}
                    value={dialogBodyProps.driveItem.name}
                    // Pre-select only the base name, so typing straight away keeps the extension.
                    getEditSelection={name => [0, name ? getDocumentNameFromPath(name).length : 0]}
                    validate={name => {
                        const cleaned = formatInputDocumentName(name || '')
                        if (!cleaned) {
                            return translate('file.pivot.document.create.name.required')
                        }
                        // Without an extension SharePoint turns the item into a folder (HAM-4101).
                        if (!getDocumentExtension(cleaned)) {
                            return translate('file.pivot.document.mutate.form.name.extension.required')
                        }
                        return undefined
                    }}
                    warning={name => {
                        const change = getExtensionChange(dialogBodyProps.driveItem.name, name)
                        return change.isChanged
                            ? translate('file.pivot.document.mutate.form.name.extension.changed', {
                                  oldExtension: change.oldExtension,
                                  newExtension: change.newExtension,
                              })
                            : undefined
                    }}
                    onSubmit={newName => {
                        return axios
                            .patch<DriveItem, AxiosResponse<DriveItem>>(
                                `/items/${dialogBodyProps.driveItem.id}`,
                                { name: formatInputDocumentName(newName) },
                                {
                                    baseURL: Destination.Documents,
                                    params: { '@microsoft.graph.conflictBehavior': 'fail' },
                                    headers: { SuppressErrors: 'true' },
                                },
                            )
                            .then(item => {
                                dialogBodyProps.driveItem.name = item.data.name
                                dialogBodyProps.callback?.()
                            })
                    }}
                    inputProps={{ className: appStyles.width100 }}
                />
            </DialogTitle>
            <DialogContent className={appStyles.overflowXHidden}>
                {!preview && loading && <Loading />}
                {preview && <Image src={preview} block bordered fit={'contain'} shape={'rounded'} />}
            </DialogContent>
            <DialogActions>
                <div className={mergeClasses(appStyles.displayFlex, appStyles.width100, appStyles.gapS, appStyles.spaceBetween)}>
                    <Tooltip relationship={'label'} withArrow content={translate('action.open')}>
                        <Button
                            appearance="subtle"
                            disabled={loading || !openingDriveItemLoaded || isOpening || editingName}
                            icon={isOpening ? <LoadingIcon /> : <OpenIcon />}
                            onClick={e => {
                                e.stopPropagation()
                                openOrDownload()
                            }}
                        />
                    </Tooltip>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <Button
                            appearance="subtle"
                            disabled={loading || !dialogBodyProps.hasPrevious?.() || editingName}
                            icon={<ArrowLeftIcon />}
                            onClick={dialogBodyProps.previous}
                        />
                        <Button
                            appearance="subtle"
                            disabled={loading || !dialogBodyProps.hasNext?.() || editingName}
                            icon={<ArrowRightIcon />}
                            onClick={dialogBodyProps.next}
                        />
                    </div>

                    <DialogTrigger disableButtonEnhancement>
                        <Button appearance="secondary" disabled={editingName} icon={<CloseIcon />} />
                    </DialogTrigger>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
